import type { Utwor } from '../store/db.ts';
import { compile } from '../chart/compile.ts';
import { suggestCapo } from '../music/latwe-chwyty.ts';

// Panel stroju (etap 7 planu): transpozycja i kapodaster. Osobny moduł od
// `wklejanie.ts`, bo to NIE jest edycja treści utworu - to ustawienie odczytu,
// które żyje w widoku grania i musi odbić się na skrócie NATYCHMIAST (pełnym
// `dopasuj()`, patrz `src/main.ts`), a nie dopiero po powrocie do menu.
//
// Ten moduł nie zna się na bazie (`zapiszUtwor`) ani na `dopasuj()` - jedynym
// stykiem na zewnątrz jest `naZmiane`, wołane po każdej zmianie wartości.
// Wywołujący decyduje, co znaczy "zmiana": zapis do bazy i przeliczenie
// skrótu (`main.ts`), nie ten moduł.

export const MIN_TRANSPOZYCJA = -6;
export const MAX_TRANSPOZYCJA = 6;
export const MIN_CAPO = 0;
export const MAX_CAPO = 7;

/**
 * Stan stroju do pokazania w wierszu metadanych, po polsku, TYLKO gdy
 * niezerowy - `transpozycja 0` byłoby szumem na ekranie, którego cała
 * obietnica brzmi "tylko to, co potrzebne". `null`, gdy oba pola są zerowe
 * (stan domyślny, nic do powiedzenia).
 */
export function formatujStanStroju(transpozycja: number, capo: number): string | null {
  const czesci: string[] = [];
  if (transpozycja !== 0) czesci.push(`${transpozycja > 0 ? '+' : ''}${transpozycja} półtony`);
  if (capo !== 0) czesci.push(`kapodaster ${capo}`);
  return czesci.length > 0 ? czesci.join(' · ') : null;
}

export interface OpcjePaneluStroju {
  utwor: Utwor;
  /** Wywoływane po KAŻDEJ zmianie (przycisk `−`/`+`/`wyzeruj`), z nowymi
   * wartościami. Panel sam nie zapisuje niczego do bazy i nie przelicza
   * skrótu - to robi wywołujący, w tym callbacku. */
  naZmiane: (transpozycja: number, capo: number) => void | Promise<void>;
}

const KLASA_NAKLADKI = 'zar-stroj-nakladka';

function przytnij(wartosc: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, wartosc));
}

/**
 * Otwiera panel stroju jako nakładkę na `host` (w aplikacji: `document.body`,
 * ŚWIADOMIE poza `#app` - patrz `main.ts`: `#app` jest niszczony i budowany od
 * zera przy każdym `replaceChildren()`, a nakładka na `body` przeżywa
 * przeliczenie treści `#tresc` wywołane przez własne przyciski panelu, więc
 * panel nie znika po każdym `+`/`−`).
 *
 * Idempotentne: kolejne wywołanie zamyka poprzednią nakładkę na TYM `host`
 * przed otwarciem nowej, żeby dwa dotknięcia wiersza metadanych nie złożyły
 * dwóch panelu jeden na drugim.
 */
export function otworzPanelStroju(host: HTMLElement, opcje: OpcjePaneluStroju): void {
  host.querySelector(`.${KLASA_NAKLADKI}`)?.remove();

  let transpozycja = opcje.utwor.transpozycja ?? 0;
  let capo = opcje.utwor.capo ?? 0;

  const naklad = document.createElement('div');
  naklad.className = KLASA_NAKLADKI;

  const karta = document.createElement('div');
  karta.className = 'zar-stroj-karta';
  naklad.appendChild(karta);

  const naglowek = document.createElement('p');
  naglowek.className = 'zar-stroj-naglowek';
  naglowek.textContent = 'strój';
  karta.appendChild(naglowek);

  /** Buduje jeden wiersz `-`/wartość/`+` i zwraca funkcję odświeżającą jego
   * wygląd (włącznie z (dis)aktywacją przycisków na granicy zakresu). Cele
   * dotyku są DUŻE (patrz CSS `.zar-stroj-przycisk`) - to się obsługuje z
   * gitarą na kolanach, z odległości wyciągniętej ręki. */
  function wiersz(
    etykieta: string,
    odczytaj: () => number,
    min: number,
    max: number,
    zmien: (delta: number) => void,
  ): () => void {
    const w = document.createElement('div');
    w.className = 'zar-stroj-wiersz';

    const lbl = document.createElement('span');
    lbl.className = 'zar-stroj-etykieta';
    lbl.textContent = etykieta;

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'zar-stroj-przycisk';
    minus.textContent = '−';
    minus.setAttribute('aria-label', `${etykieta}: mniej`);

    const wart = document.createElement('span');
    wart.className = 'zar-stroj-wartosc';

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'zar-stroj-przycisk';
    plus.textContent = '+';
    plus.setAttribute('aria-label', `${etykieta}: więcej`);

    const odswiez = (): void => {
      const v = odczytaj();
      wart.textContent = String(v);
      minus.disabled = v <= min;
      plus.disabled = v >= max;
    };

    // `stopPropagation` na obu przyciskach: warunek działania, nie
    // ostrożność. `naklad` zamyka się na dotknięciu poza kartą (niżej), a bez
    // tego dotknięcie `+`/`−` (wewnątrz karty, ale to samo zdarzenie) dobiłoby
    // bąbelkowaniem do tego samego nasłuchu i zamknęło panel tym samym gestem,
    // którym miało zmienić wartość.
    minus.addEventListener('click', (z) => { z.stopPropagation(); zmien(-1); odswiez(); });
    plus.addEventListener('click', (z) => { z.stopPropagation(); zmien(1); odswiez(); });

    w.append(lbl, minus, wart, plus);
    karta.appendChild(w);
    odswiez();
    return odswiez;
  }

  // `wiersz()` zwraca swoją funkcję odświeżającą - `wyzeruj` niżej trzyma OBIE,
  // bo musi odświeżyć oba wiersze naraz, nie tylko ten, którego dotyczył gest.
  const odswiezTranspozycje = wiersz(
    'transpozycja',
    () => transpozycja,
    MIN_TRANSPOZYCJA,
    MAX_TRANSPOZYCJA,
    (delta) => {
      transpozycja = przytnij(transpozycja + delta, MIN_TRANSPOZYCJA, MAX_TRANSPOZYCJA);
      void opcje.naZmiane(transpozycja, capo);
    },
  );
  const odswiezCapo = wiersz(
    'kapodaster',
    () => capo,
    MIN_CAPO,
    MAX_CAPO,
    (delta) => {
      capo = przytnij(capo + delta, MIN_CAPO, MAX_CAPO);
      void opcje.naZmiane(transpozycja, capo);
    },
  );

  // "łatwiejsze chwyty": podpowiedź pozycji kapodastera, licząca się z
  // `src/music/latwe-chwyty.ts` po unikalnych akordach CAŁEGO utworu -
  // `compile()` wołane tu, na żądanie, a nie przy każdym otwarciu panelu,
  // bo to jedyne miejsce, które go potrzebuje, i nie każdy grający kliknie
  // ten przycisk.
  const sugestiePrzycisk = document.createElement('button');
  sugestiePrzycisk.type = 'button';
  sugestiePrzycisk.className = 'zar-przycisk zar-przycisk--wtorny';
  sugestiePrzycisk.textContent = 'łatwiejsze chwyty';
  karta.appendChild(sugestiePrzycisk);

  const sugestieLista = document.createElement('div');
  sugestieLista.className = 'zar-stroj-sugestie';
  sugestieLista.hidden = true;
  karta.appendChild(sugestieLista);

  sugestiePrzycisk.addEventListener('click', (z) => {
    z.stopPropagation();
    sugestieLista.replaceChildren();

    const chart = compile(opcje.utwor.id, opcje.utwor.rawTab);
    const zbiorAkordow = new Set<string>();
    for (const sekcja of chart.sections) {
      for (const token of sekcja.progression.raw) zbiorAkordow.add(token.trim());
    }
    const sugestie = suggestCapo([...zbiorAkordow], capo);

    if (sugestie.length === 0) {
      const brak = document.createElement('p');
      brak.className = 'zar-stroj-sugestia-brak';
      brak.textContent = 'bieżąca pozycja kapodastera jest już najłatwiejsza';
      sugestieLista.appendChild(brak);
    } else {
      for (const sugestia of sugestie) {
        const wierszSugestii = document.createElement('button');
        wierszSugestii.type = 'button';
        wierszSugestii.className = 'zar-stroj-sugestia';
        wierszSugestii.textContent = `capo ${sugestia.capo}: ${sugestia.chords.join(', ')}`;
        wierszSugestii.addEventListener('click', (zz) => {
          zz.stopPropagation();
          capo = przytnij(sugestia.capo, MIN_CAPO, MAX_CAPO);
          odswiezCapo();
          sugestieLista.hidden = true;
          void opcje.naZmiane(transpozycja, capo);
        });
        sugestieLista.appendChild(wierszSugestii);
      }
    }

    sugestieLista.hidden = false;
  });

  const akcje = document.createElement('div');
  akcje.className = 'zar-stroj-akcje';

  const wyzeruj = document.createElement('button');
  wyzeruj.type = 'button';
  wyzeruj.className = 'zar-przycisk zar-przycisk--wtorny';
  wyzeruj.textContent = 'wyzeruj';
  wyzeruj.addEventListener('click', (z) => {
    z.stopPropagation();
    transpozycja = 0;
    capo = 0;
    odswiezTranspozycje();
    odswiezCapo();
    void opcje.naZmiane(transpozycja, capo);
  });

  const gotowe = document.createElement('button');
  gotowe.type = 'button';
  gotowe.className = 'zar-przycisk zar-przycisk--glowny';
  gotowe.textContent = 'gotowe';
  gotowe.addEventListener('click', (z) => { z.stopPropagation(); naklad.remove(); });

  akcje.append(wyzeruj, gotowe);
  karta.appendChild(akcje);

  // Zamknięcie dotknięciem POZA panelem: nasłuch na nakładce (całość ekranu),
  // nie na karcie. `stopPropagation` na karcie jest warunkiem, żeby dotknięcie
  // WEWNĄTRZ karty (poza przyciskami, np. etykiety) nie zamykało panelu.
  naklad.addEventListener('click', () => naklad.remove());
  karta.addEventListener('click', (z) => z.stopPropagation());

  host.appendChild(naklad);
}
